// Profile › Bank Account hub (spec §3, §5). Nine entries in spec order with a
// one-line status under each, plus the red re-KYC restriction banner at top.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faChevronRight, faVault, faMoneyCheck, faTriangleExclamation,
  faFlag, faIdCard, faUsers, faStar, faFileInvoice, faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { rekyc, lockers, chequeBooks, disputes } from '@/api/bankServices';
import { runBankClock, REKYC_RESTRICT_MSG } from '@/mock/phase4';
import '../phase4.css';

const BankHome = () => {
  const navigate = useNavigate();
  const [rekycStatus, setRekycStatus] = useState<{ line: string; amber: boolean; restricted: boolean } | null>(null);
  const [lockerLine, setLockerLine] = useState('');
  const [chequeLine, setChequeLine] = useState('');
  const [openDisputes, setOpenDisputes] = useState(0);

  useEffect(() => {
    runBankClock();
    rekyc()
      .then((r) => {
        const due = new Date(r.dueAt).toLocaleDateString('en-IN');
        if (r.status === 'UP_TO_DATE') setRekycStatus({ line: 'Up to date', amber: false, restricted: false });
        else if (r.status === 'DUE') setRekycStatus({ line: `Due on ${due}`, amber: true, restricted: false });
        else setRekycStatus({ line: `Overdue since ${due}`, amber: true, restricted: r.restrictsDebits });
      })
      .catch(() => setRekycStatus(null));
    lockers()
      .then(({ lockers: mine, waitlist }) => {
        if (mine.length > 0) setLockerLine(`Locker ${mine[0].number}`);
        else if (waitlist.some((w) => w.offered)) setLockerLine('Locker available — accept it now');
        else if (waitlist.length > 0) setLockerLine(`Wait list #${waitlist[0].waitNumber}`);
      })
      .catch(() => undefined);
    chequeBooks()
      .then((books) => {
        const ip = books.find((b) => !b.delivered);
        if (ip) setChequeLine(`Cheque book ${ip.stage.toLowerCase()}`);
      })
      .catch(() => undefined);
    disputes().then((d) => setOpenDisputes(d.filter((x) => x.status !== 'RESOLVED').length)).catch(() => undefined);
  }, []);

  const rows = [
    { icon: faVault, title: 'Reserve a Locker', sub: lockerLine, to: '/profile/bank/lockers' },
    { icon: faMoneyCheck, title: 'Cheque Book Services', sub: chequeLine, to: '/profile/bank/cheques' },
    { icon: faTriangleExclamation, title: 'IMPS Dispute', sub: openDisputes ? `${openDisputes} open` : '', to: '/profile/bank/imps-dispute' },
    { icon: faFlag, title: 'Raise a Dispute', sub: 'Or report fraud', to: '/profile/bank/dispute' },
    { icon: faIdCard, title: 'Re-KYC', sub: rekycStatus?.line, amber: rekycStatus?.amber, to: '/profile/bank/rekyc' },
    { icon: faUsers, title: 'View/Add Nominee Details', sub: 'Read-only for now', to: '/profile/bank/nominee' },
    { icon: faStar, title: 'Favourites', sub: 'Pinned payees and billers', to: '/profile/bank/favourites' },
    { icon: faFileInvoice, title: 'Update GST', sub: 'GSTIN on your account', to: '/profile/bank/gst' },
    { icon: faGlobe, title: 'FATCA Update', sub: 'Tax residency', to: '/profile/bank/fatca' },
  ];

  return (
    <div className="nova-p4-page">
      <div className="nova-p4-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Bank Account</h2>
      </div>

      {rekycStatus?.restricted && (
        <div className="nova-p4-banner red">{REKYC_RESTRICT_MSG}</div>
      )}
      {rekycStatus && rekycStatus.amber && !rekycStatus.restricted && (
        <div className="nova-p4-banner amber">{rekycStatus.line}. Complete Re-KYC to keep your account fully usable.</div>
      )}

      {rows.map((r) => (
        <button key={r.title} type="button" className="nova-p4-row" onClick={() => navigate(r.to)}>
          <span className="nova-p4-row-icon"><FontAwesomeIcon icon={r.icon} /></span>
          <span className="nova-p4-row-text">
            <span className="nova-p4-row-title">{r.title}</span>
            {r.sub && <span className="nova-p4-row-sub" style={r.amber ? { color: '#b26a00', fontWeight: 600 } : undefined}>{r.sub}</span>}
          </span>
          <FontAwesomeIcon icon={faChevronRight} className="nova-p4-chevron" />
        </button>
      ))}
    </div>
  );
};

export default BankHome;
